import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Save, X, Sparkles } from "lucide-react";
import { useAnnotations } from "@/hooks/useAnnotations";
import { useLocation } from "react-router-dom";
import { useBrandManager } from "@/hooks/useBrandManager";
import { UnifiedLLMService } from "@/services/llm/UnifiedLLMService";
import { ApiCallsAuditService } from "@/services/apiCallsAuditService";
import { useToast } from "@/hooks/use-toast";

interface AnnotationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  capturedImage: Blob | null;
  containerId?: string;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export const AnnotationDialog = ({
  isOpen,
  onClose,
  capturedImage,
  containerId,
}: AnnotationDialogProps) => {
  const location = useLocation();
  const { createAnnotation, isCreating } = useAnnotations();
  const { currentBrand } = useBrandManager();
  const { toast } = useToast();
  
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [tags, setTags] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSuggesting, setIsSuggesting] = useState(false);

  useEffect(() => {
    if (capturedImage) {
      const url = URL.createObjectURL(capturedImage);
      setImagePreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setImagePreview(null);
    }
  }, [capturedImage]);

  const handleSave = async () => {
    if (!capturedImage) return;

    const tagsArray = tags
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    const success = await createAnnotation({
      imageBlob: capturedImage,
      title: title || undefined,
      comment: comment || undefined,
      tags: tagsArray.length > 0 ? tagsArray : undefined,
      pageRoute: location.pathname,
      containerId,
    });

    if (success) {
      handleClose();
    }
  };

  const handleClose = () => {
    setTitle("");
    setComment("");
    setTags("");
    onClose();
  };

  const handleSuggest = async () => {
    if (!capturedImage) return;
    const brandId = currentBrand?.id ?? "unknown";
    setIsSuggesting(true);
    ApiCallsAuditService.startTracking();
    try {
      const dataUrl = await blobToDataUrl(capturedImage);
      const response = await UnifiedLLMService.callLLM({
        brandId,
        source: "annotation_suggestions",
        provider: "openai",
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content:
              "You are a helpful assistant. Given a screenshot of a dashboard widget or report, suggest a short annotation comment (1-2 sentences) and comma-separated tags. Include tags like analytics, dashboard, api-audit when relevant. Return ONLY valid JSON with keys: comment (string), tags (string, comma-separated). No markdown, no explanation.",
          },
          {
            role: "user",
            content: [
              {
                type: "text",
                text: `Context: annotation snapshot. Container: ${containerId ?? "unknown"}. Look at the image and suggest a comment and tags. Return JSON: { "comment": "...", "tags": "tag1, tag2, tag3" }`,
              },
              { type: "image_url", image_url: { url: dataUrl } },
            ],
          },
        ],
        max_tokens: 300,
      });

      const costEstimate = response.usage
        ? ApiCallsAuditService.estimateCost("openai", response.usage, response.model)
        : 0;
      await ApiCallsAuditService.logApiCall({
        source_menu_item: "annotation_suggestions",
        platform: (response.provider as any) || "openai",
        brand_id: brandId !== "unknown" ? brandId : undefined,
        model_used: response.model,
        status_code: 200,
        metadata: { containerId },
        cost_estimate: costEstimate,
      });

      const raw = response.content.trim();
      const jsonMatch = raw.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]) as { comment?: string; tags?: string; title?: string };
        if (parsed.comment) setComment(parsed.comment);
        if (parsed.tags) setTags(parsed.tags);
        if (parsed.title) setTitle(parsed.title);
      } else {
        toast({ title: "Could not parse suggestions", variant: "destructive" });
      }
    } catch (err) {
      console.error(err);
      toast({ title: "Failed to generate suggestions", variant: "destructive" });
    } finally {
      setIsSuggesting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogTitleWithBug
          popupName="Create Annotation"
          popupContext={location.pathname}
          isOpen={isOpen}
        >
          Save Annotation
        </DialogTitleWithBug>

        <div className="space-y-4">
          {/* Image Preview */}
          {imagePreview && (
            <div className="border rounded-lg overflow-hidden bg-muted">
              <img
                src={imagePreview}
                alt="Captured screenshot"
                className="w-full max-h-64 object-contain"
              />
            </div>
          )}

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="annotation-title">Title (optional)</Label>
            <Input
              id="annotation-title"
              placeholder="Give this annotation a title..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Label htmlFor="annotation-comment">Comment</Label>
            <div className="relative">
              <Textarea
                id="annotation-comment"
                placeholder="Add your notes about this snapshot..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8 text-purple-500 hover:text-purple-600 hover:bg-purple-500/10"
                onClick={handleSuggest}
                disabled={!capturedImage || isSuggesting}
                title="Suggest comment and tags"
              >
                {isSuggesting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label htmlFor="annotation-tags">Tags (optional)</Label>
            <Input
              id="annotation-tags"
              placeholder="Comma-separated tags, e.g. important, analytics, q1"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleClose} disabled={isCreating}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isCreating || !capturedImage}>
              {isCreating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Annotation
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
